import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageFileUpload from 'components/ImageFileUpload/ImageFileUpload';
import xeniaApi from 'api/index';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CameraIconSharpEdged from 'components/Icons/cameraIconSharpEdged';
import CustomButton from 'components/Button/CustomButton';
import CustomToolTip from 'components/Tooltip';
import DIALOGS from 'utils/dialogIds';
import actions from 'store/actions';
import { useDispatch } from 'react-redux';

const ImageUploaderSection = ({
  announcement,
  photos,
  setPhotos,
  readOnly,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const photoLimit = announcement?.photoLimit || 1;
  const isMultipleAllowed = announcement?.allowMultiplePhotos || false;
  const remainingPhotos = photoLimit - photos.length;

  const handleUploadImage = async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    try {
      setUploading(true);
      setError(null);

      // Upload all images to server
      const response = await xeniaApi.CreateTaskAttchmentFilesApi(
        acceptedFiles,
      );

      // Process response to get file URLs
      const uploadedPhotos = response.filePath.map((attachment, index) => ({
        id: `photo-${Date.now()}-${index}`,
        name: acceptedFiles[index].name,
        type: acceptedFiles[index].type,
        size: acceptedFiles[index].size,
        fileUrl: response.baseUrl + attachment,
        file: acceptedFiles[index],
      }));

      // If single photo mode, replace existing photo
      if (!isMultipleAllowed) {
        setPhotos(uploadedPhotos.slice(0, 1));
      } else {
        // Only add up to the remaining limit
        const newPhotos = [
          ...photos,
          ...uploadedPhotos.slice(0, remainingPhotos),
        ];
        setPhotos(newPhotos);
      }
    } catch (err) {
      console.error('Error uploading images:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (e, index) => {
    e.stopPropagation();
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleViewFullscreen = (photo) => {
    dispatch(
      actions.setDialog({
        dialogId: DIALOGS.PHOTOS_ATTACHMENTS_PREVIEW,
        open: true,
        data: {
          allAttachments: photos,
          id: photo.fileUrl || photo.url,
          removeItem: handleRemovePhoto,
        },
      }),
    );
  };

  return (
    <Box
      sx={{
        mb: 3,
        background: '#fff',
        border: '1px solid #EEEEEE',
        borderRadius: '10px',
        padding: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="subtitle1" fontWeight="700" fontSize={'14px'}>
        {(isMultipleAllowed && photos.length === photoLimit) ||
        (!isMultipleAllowed && photos.length > 0) ? (
          <CheckIcon sx={{ color: '#04B88D', mr: 1 }} />
        ) : (
          <CameraIconSharpEdged
            sx={{
              mr: 1,
              '& path': { fill: '#4E48FA' },
              '& circle': { stroke: '#4E48FA' },
            }}
          />
        )}
        {isMultipleAllowed
          ? `Submit Photos (${photos.length}/${photoLimit})`
          : 'Submit Photo'}
      </Typography>
      <Box display={'flex'} alignItems="center" gap={1}>
        {/* Photo Grid */}
        {photos.length > 0 && (
          <Box display={'flex'} alignItems="center" gap={1}>
            {photos.map((photo, index) => (
              <CustomToolTip
                title={photo.name ?? 'View Photo'}
                placement="top"
                key={index}
              >
                <Box
                  key={photo.id || index}
                  onClick={() => handleViewFullscreen(photo)}
                  sx={{
                    position: 'relative',
                    width: '60px',
                    height: '60px',
                    borderRadius: '10px',
                    backgroundImage: `url(${photo.fileUrl || photo.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    cursor: 'pointer',
                  }}
                />
              </CustomToolTip>
            ))}
          </Box>
        )}
        {/* Upload Button (Only show if there's room for more photos) */}
        {((!isMultipleAllowed && photos.length === 0) ||
          (isMultipleAllowed && photos.length < photoLimit)) &&
          !readOnly && (
            <Box>
              <ImageFileUpload
                onUploadCallback={handleUploadImage}
                options={{
                  accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
                  maxFiles: 1,
                  maxSize: 5242880, // 5MB
                }}
                isPreview={announcement.status !== 'active'}
              >
                <CustomButton
                  variant="contained"
                  buttonLoader={uploading}
                  disabled={uploading || announcement.status !== 'active'}
                  sx={{
                    borderRadius: '6px',
                    padding: '5px 12px',
                    fontSize: 12,
                    textTransform: 'none',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Take Photo'}
                </CustomButton>
              </ImageFileUpload>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 1 }}
                >
                  {error}
                </Typography>
              )}
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default ImageUploaderSection;
